/**
 * PATH: packages/email-hub/src/feature/reader-header/responsive/density.ts
 * FILE_ID: EMAILHUB-READERHEADER-RESPONSIVE-DENSITY-001
 * PURPOSE: Resolve whole-reader width into stable responsive tiers.
 * ROLE IN ARCHITECTURE: Container-query adapter shared by header leaves.
 * MAIN EXPORTS: densityForReaderWidth, useReaderHeaderDensity.
 * NON-RESPONSIBILITIES: This file does not choose which product actions exist.
 * NOTES FOR FUTURE AI: Measure the whole reader, not the avatar-indented child; preserve hysteresis.
 *
 *   The hook reports `measured` alongside the tier because the value before
 *   the first ResizeObserver read is a GUESS ('standard'). Anything that
 *   MOUNTS on the strength of the tier must wait for `measured` — change #294
 *   opens the context lanes by default on non-compact readers, and acting on
 *   the guess mounted then tore down the whole lane subtree on narrow
 *   viewports (visible flash, and duplicate controls during the collapse tail).
 */
import {
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react'

export type ReaderHeaderDensity = 'compact' | 'standard' | 'wide' | 'expanded'

export const READER_STANDARD_MIN = 480
export const READER_WIDE_MIN = 840
export const READER_EXPANDED_MIN = 1200
export const READER_DENSITY_HYSTERESIS = 16

export function densityForReaderWidth(width: number): ReaderHeaderDensity {
  if (width >= READER_EXPANDED_MIN) return 'expanded'
  if (width >= READER_WIDE_MIN) return 'wide'
  if (width >= READER_STANDARD_MIN) return 'standard'
  return 'compact'
}

/**
 * Resolve the next tier for a width, holding the current tier inside the
 * hysteresis window around the crossed boundary. Exported so the kill-test
 * can drive simulated resize sequences without a ResizeObserver.
 */
export function nextDensity(
  current: ReaderHeaderDensity,
  width: number,
): ReaderHeaderDensity {
  const raw = densityForReaderWidth(width)
  if (raw === current) return current

  const floors: Record<ReaderHeaderDensity, number> = {
    compact: 0,
    standard: READER_STANDARD_MIN,
    wide: READER_WIDE_MIN,
    expanded: READER_EXPANDED_MIN,
  }
  const movingUp = floors[raw] > floors[current]
  const boundary = movingUp ? floors[raw] : floors[current]
  if (Math.abs(width - boundary) < READER_DENSITY_HYSTERESIS) return current
  return raw
}

/** Tuple: [ref to measure, current tier, whether the tier is a real measurement]. */
export function useReaderHeaderDensity(): [
  RefObject<HTMLDivElement | null>,
  ReaderHeaderDensity,
  boolean,
] {
  const ref = useRef<HTMLDivElement | null>(null)
  const [state, setState] = useState<{
    density: ReaderHeaderDensity
    measured: boolean
  }>({ density: 'standard', measured: false })

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const update = () =>
      setState((current) => {
        const density = nextDensity(current.density, element.clientWidth)
        if (current.measured && density === current.density) return current
        return { density, measured: true }
      })
    update()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update)
      return () => window.removeEventListener('resize', update)
    }
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return [ref, state.density, state.measured]
}
